<script setup>
import {computed, ref} from "vue";

const props = defineProps({
    lead: {
        type: Object,
        required: true,
    },
})

const showNina = ref(false)
const showQuote = ref(false)

const sanitizedDomain = computed(() => {
    if (props.lead.website) {
        try {
            const url = props.lead.website.includes('://')
                ? props.lead.website
                : 'https://' + props.lead.website;
            return new URL(url).hostname.replace(/^www\./, '');
        } catch {
            return props.lead.website.replace(/^www\./, '');
        }
    }
    if (props.lead.email) {
        const parts = props.lead.email.split('@');
        if (parts.length === 2) {
            return parts[1];
        }
    }
    return null;
})

const canShowCloserReport = computed(() => {
    return ! props.lead.has_generic_email;
})

const hasCloserHired = computed(() => {
    return window.myflow?.company?.hired_specialists?.includes('nina') === true;
})

const closerUnavailableTooltip = computed(() => {
    return hasCloserHired.value ? null : _mft('crm:nina.requiresCloserHired');
})

const toggleCloserReport = () => {
    if (! hasCloserHired.value) {
        return;
    }

    showNina.value = ! showNina.value;
}

const toggleCloserQuote = () => {
    if (! hasCloserHired.value) {
        return;
    }

    showQuote.value = ! showQuote.value;
}
</script>

<template>
    <div class="flex items-center space-x-2">
        <container-with-tooltip
            v-if="canShowCloserReport || ! hasCloserHired"
            :class="{ 'cursor-not-allowed': ! hasCloserHired }"
            additional-tooltip-class="whitespace-nowrap"
            alignment="right"
            theme="dark"
        >
            <button-base
                :style-type="hasCloserHired ? 'secondary' : 'disabled'"
                :disabled="! hasCloserHired"
                @click="toggleCloserReport"
            >
                <template #icon>
                    <i class="scale-125 fa-regular fa-head-side-brain"></i>
                </template>
                <span class="hidden md:block ml-1.5">
                    <mf-translate domainkey="crm:nina.closerReport"></mf-translate>
                </span>
            </button-base>

            <template
                v-if="! hasCloserHired"
                #tooltip
            >
                <div>
                    {{ closerUnavailableTooltip }}
                </div>
            </template>
        </container-with-tooltip>

        <container-with-tooltip
            :class="{ 'cursor-not-allowed': ! hasCloserHired }"
            additional-tooltip-class="whitespace-nowrap"
            alignment="right"
            theme="dark"
        >
            <button-base
                :style-type="hasCloserHired ? 'secondary' : 'disabled'"
                :disabled="! hasCloserHired"
                @click="toggleCloserQuote"
            >
                <template #icon>
                    <i class="scale-125 fa-regular fa-file-signature"></i>
                </template>
                <span class="hidden md:block ml-1.5">
                    <mf-translate domainkey="crm:nina.closerQuote"></mf-translate>
                </span>
            </button-base>

            <template
                v-if="! hasCloserHired"
                #tooltip
            >
                <div>
                    {{ closerUnavailableTooltip }}
                </div>
            </template>
        </container-with-tooltip>

        <nina-slideout
            v-if="showNina"
            :lead-id="lead.id"
            :lead-name="lead.name"
            :lead-company="lead.company"
            :domain="sanitizedDomain"
            @close="showNina = false"
        />

        <closer-quote-slideout
            v-if="showQuote"
            :lead-id="lead.id"
            :lead-name="lead.name"
            :lead-email="lead.email"
            :lead-company="lead.company"
            :domain="sanitizedDomain"
            @close="showQuote = false"
        />
    </div>
</template>
